'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

const products = [
{
  id: 'air-freight',
  title: 'Air Freight Services',
  category: 'Express Shipping',
  description: 'Fast and reliable air cargo services for time-sensitive shipments worldwide',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d723d68d-1770422413055.png",
  alt: 'Cargo airplane on runway ready for international air freight shipping',
  features: ['24-48 Hour Delivery', 'Global Coverage', 'Temperature Control', 'Real-time Tracking'],
  price: 'From $150/kg'
},
{
  id: 'ocean-freight',
  title: 'Ocean Freight',
  category: 'Bulk Shipping',
  description: 'Cost-effective sea freight solutions for large volume shipments and containers',
  image: "https://images.unsplash.com/photo-1649358645704-ce277199c2d2",
  alt: 'Large cargo container ship at sea port for ocean freight logistics',
  features: ['FCL & LCL Options', 'Port-to-Port', 'Customs Clearance', 'Insurance Coverage'],
  price: 'From $800/container'
},
{
  id: 'ground-transport',
  title: 'Ground Transportation',
  category: 'Road Freight',
  description: 'Reliable truck and rail services for domestic and cross-border deliveries',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12a364d9b-1769413188949.png",
  alt: 'Fleet of modern logistics trucks for ground transportation services',
  features: ['Door-to-Door', 'Scheduled Routes', 'Hazmat Certified', 'GPS Tracking'],
  price: 'From $2/mile'
},
{
  id: 'warehousing',
  title: 'Warehousing Solutions',
  category: 'Storage & Distribution',
  description: 'Secure storage facilities with advanced inventory management systems',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_122b22f9d-1764654516001.png",
  alt: 'Modern warehouse interior with organized storage racks and inventory systems',
  features: ['Climate Control', '24/7 Security', 'Inventory Management', 'Pick & Pack'],
  price: 'From $500/month'
},
{
  id: 'customs-brokerage',
  title: 'Customs Brokerage',
  category: 'Compliance',
  description: 'Expert customs clearance and documentation services for international trade',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_112d34302-1770422409905.png",
  alt: 'Customs documentation and international trade paperwork on desk',
  features: ['Documentation', 'Duty Calculation', 'Compliance Check', 'Fast Processing'],
  price: 'From $75/shipment'
},
{
  id: 'last-mile',
  title: 'Last Mile Delivery',
  category: 'Final Delivery',
  description: 'Efficient final-mile delivery solutions with flexible scheduling options',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19724dcf8-1768102476962.png",
  alt: 'Delivery person with package at customer doorstep for last mile service',
  features: ['Same-Day Options', 'Signature Required', 'Photo Proof', 'Time Windows'],
  price: 'From $8/delivery'
}];


export default function ProductsGallery() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const categories = ['All', ...Array.from(new Set(products.map((p) => p.category)))];

  const filteredProducts = selectedCategory === 'All' ?
  products :
  products?.filter((p) => p?.category === selectedCategory);

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-12 justify-center">
          {categories?.map((category) =>
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-6 py-2 rounded-full font-medium transition-all ${
            selectedCategory === category ?
            'bg-primary text-primary-foreground shadow-md' :
            'bg-card text-foreground border border-border hover:border-primary'}`
            }>
            
              {category}
            </button>
          )}
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts?.map((product) =>
          <div
            key={product?.id}
            className="group bg-card rounded-xl overflow-hidden border border-border hover:shadow-card-hover transition-all duration-300 hover:scale-105">
            
              {/* Product Image */}
              <div className="relative h-56 overflow-hidden">
                <AppImage
                src={product?.image}
                alt={product?.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              
                <div className="absolute top-4 right-4 px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                  {product?.category}
                </div>
              </div>

              {/* Product Details */}
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  {product?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {product?.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-4">
                  {product?.features?.map((feature, index) =>
                <li key={index} className="flex items-center gap-2 text-sm text-foreground">
                      <Icon name="CheckCircleIcon" size={16} className="text-success" />
                      <span>{feature}</span>
                    </li>
                )}
                </ul>

                {/* Price & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <span className="text-lg font-semibold text-primary">{product?.price}</span>
                  <Link
                  href={`/order?service=${product?.id}`}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-all flex items-center gap-2">
                  
                    <span>Order Now</span>
                    <Icon name="ArrowRightIcon" size={16} />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}